import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../services/apiClient";
import { API_BASE_URL } from "../config";
import { useI18n } from "../providers/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import { hasAnyRole, ROLE } from "../utils/roles";
import "../styles/LeaveDetailsModal.css";

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;

const TRAVEL_TYPE_NAME_TR = "görev/seyahat izni";

const isTravelLeave = (leaveLike) => {
  const name = String(leaveLike?.leave_type_name || "")
    .trim()
    .toLocaleLowerCase("tr-TR");
  return name === TRAVEL_TYPE_NAME_TR;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : "—");

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const formatTime = (value) => (value ? String(value).slice(0, 5) : "");

const formatHalfDay = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (v === "AM") return "Öğleden önce";
  if (v === "PM") return "Öğleden sonra";
  return "";
};

const formatMoney = (value, currency) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${num.toFixed(2)} ${currency || ""}`.trim();
};

const TRANSPORT_LABELS = {
  plane: "Uçak",
  train: "Tren",
  bus: "Otobüs",
  car_rental: "Araç kiralama",
};

const prettyTransport = (value) => {
  if (!value) return "—";
  return TRANSPORT_LABELS[value] || value;
};

const prettyStatus = (value, t) => {
  if (!value) return "—";
  const lower = String(value).toLocaleLowerCase("tr-TR");
  if (lower === "beklemede") return t("leave.status.pending");
  if (lower === "onaylandı") return t("leave.status.approved");
  if (lower === "reddedildi") return t("leave.status.rejected");
  if (lower === "atlandı") return t("leave.status.skipped");
  if (lower === "i̇ptal edildi" || lower === "iptal edildi") return t("leave.status.cancelled");
  return value;
};

const prettyStage = (value, t) => {
  const v = String(value || "").toLowerCase();
  if (v === "unit_supervisor" || v === "manager") return t("leave.stage.unitSupervisor");
  if (v === "company_manager") return t("leave.stage.companyManager");
  if (v === "hr") return t("leave.stage.hr");
  return value || "—";
};

const LeaveDetailsModal = ({ leaveId, initial, onClose }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [leave, setLeave] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [feedback, setFeedback] = useState(null); // {type, message}
  const [perDiemDraft, setPerDiemDraft] = useState("");
  const [savingPerDiem, setSavingPerDiem] = useState(false);
  const fileInputRefs = useRef({});

  const refreshLeave = async () => {
    if (!leaveId) return;
    try {
      const res = await apiClient.get(`/leaves/${leaveId}`);
      setLeave(res.data);
    } catch (_) {
      // ignore — caller already shows feedback
    }
  };

  useEffect(() => {
    if (!leaveId) return;
    let mounted = true;
    setLoading(true);
    setError("");
    apiClient
      .get(`/leaves/${leaveId}`)
      .then((res) => {
        if (!mounted) return;
        setLeave(res.data);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.msg || err?.message || t("admin.leaves.notLoaded"));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [leaveId, t]);

  const handleReceiptPick = (itemId) => {
    const input = fileInputRefs.current[itemId];
    if (input) input.click();
  };

  const handleReceiptChange = async (itemId, e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;
    if (String(file.type || "").toLowerCase() !== "application/pdf") {
      setFeedback({
        type: "error",
        message: t("admin.leaves.expenseReceiptOnlyPdf"),
      });
      return;
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      setFeedback({
        type: "error",
        message: t("admin.leaves.expenseReceiptTooLarge"),
      });
      return;
    }
    setUploadingItemId(itemId);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post(
        `/leaves/${leaveId}/travel-expense-items/${itemId}/receipt-pdf`,
        formData
      );
      setFeedback({
        type: "success",
        message: t("admin.leaves.expenseReceiptUploaded"),
      });
      await refreshLeave();
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err?.response?.data?.msg ||
          err?.message ||
          t("admin.leaves.expenseReceiptUploadFailed"),
      });
    } finally {
      setUploadingItemId(null);
    }
  };

  const handleReceiptDownload = (itemId) => {
    const url = `${API_BASE_URL}/leaves/${leaveId}/travel-expense-items/${itemId}/receipt-pdf`;
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      setFeedback({
        type: "error",
        message: t("admin.leaves.expenseReceiptDownloadFailed"),
      });
    }
  };

  const isTravel = useMemo(() => isTravelLeave(leave), [leave]);
  const travelExpense = leave?.travel_expense || null;
  const expenseItems = Array.isArray(travelExpense?.items) ? travelExpense.items : [];

  const isPendingLeave =
    String(leave?.status || "").trim().toLocaleLowerCase("tr-TR") === "beklemede";
  const canEditPerDiem =
    isTravel &&
    !!travelExpense &&
    travelExpense.trip_type !== "intracity" &&
    isPendingLeave &&
    hasAnyRole(user, [ROLE.ADMIN, ROLE.COMPANY_MANAGER, ROLE.HR]);

  useEffect(() => {
    if (travelExpense?.per_diem_daily_amount != null) {
      setPerDiemDraft(String(travelExpense.per_diem_daily_amount));
    }
  }, [travelExpense?.per_diem_daily_amount]);

  const handleSavePerDiem = async () => {
    const value = Number(perDiemDraft);
    if (!Number.isFinite(value) || value < 0) {
      setFeedback({ type: "error", message: t("admin.leaves.perDiemInvalid") });
      return;
    }
    setSavingPerDiem(true);
    setFeedback(null);
    try {
      await apiClient.patch(`/leaves/${leaveId}/travel-expense/per-diem`, {
        per_diem_daily_amount: value,
      });
      setFeedback({ type: "success", message: t("admin.leaves.perDiemUpdated") });
      await refreshLeave();
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err?.response?.data?.msg || err?.message || t("admin.leaves.perDiemUpdateFailed"),
      });
    } finally {
      setSavingPerDiem(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal modal-leave-details"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="leave-details-header">
          <h3>{t("admin.leaves.detailsTitle")}</h3>
          <button type="button" className="ghost" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>

        {loading && <p>{t("common.loading")}</p>}
        {error && <p className="error">{error}</p>}
        {feedback && (
          <p className={`leave-details-feedback ${feedback.type}`}>{feedback.message}</p>
        )}

        {leave && (
          <div className="leave-details-body">
            <section>
              <h4>{t("admin.leaves.sectionRequester")}</h4>
              <dl>
                <div>
                  <dt>{t("admin.leaves.code")}</dt>
                  <dd className="mono">{leave.request_code || `#${leave.id}`}</dd>
                </div>
                <div>
                  <dt>{t("admin.leaves.columns.requester")}</dt>
                  <dd>
                    {[leave.user_name, leave.user_surname].filter(Boolean).join(" ")}
                    {leave.user_username ? ` (${leave.user_username})` : ""}
                  </dd>
                </div>
                {leave.team_name && (
                  <div>
                    <dt>{t("teamLeaves.columns.team")}</dt>
                    <dd>{leave.team_name}</dd>
                  </div>
                )}
                {(leave.user_department || leave.department) && (
                  <div>
                    <dt>{t("admin.users.fields.department")}</dt>
                    <dd>{leave.user_department || leave.department}</dd>
                  </div>
                )}
                {(leave.user_section || leave.section) && (
                  <div>
                    <dt>{t("admin.users.fields.section")}</dt>
                    <dd>{leave.user_section || leave.section}</dd>
                  </div>
                )}
                <div>
                  <dt>{t("admin.leaves.createdAt")}</dt>
                  <dd className="mono">{formatDateTime(leave.created_at)}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h4>{t("admin.leaves.sectionLeave")}</h4>
              <dl>
                <div>
                  <dt>{t("admin.leaves.columns.type")}</dt>
                  <dd>
                    {leave.leave_type_name}
                    {leave.leave_sub_type_name ? ` (${leave.leave_sub_type_name})` : ""}
                  </dd>
                </div>
                <div>
                  <dt>{t("admin.leaves.start")}</dt>
                  <dd className="mono">{formatDate(leave.start_date)}</dd>
                </div>
                <div>
                  <dt>{t("admin.leaves.end")}</dt>
                  <dd className="mono">{formatDate(leave.end_date)}</dd>
                </div>
                {(leave.start_time || leave.end_time) && (
                  <div>
                    <dt>{t("leave.form.startTime")} / {t("leave.form.endTime")}</dt>
                    <dd className="mono">
                      {formatTime(leave.start_time)} - {formatTime(leave.end_time)}
                    </dd>
                  </div>
                )}
                {formatHalfDay(leave.half_day_period) && (
                  <div>
                    <dt>Yarım gün</dt>
                    <dd>{formatHalfDay(leave.half_day_period)}</dd>
                  </div>
                )}
                <div>
                  <dt>{t("admin.leaves.columns.duration")}</dt>
                  <dd>
                    {leave.total_duration_hours != null
                      ? `${Number(leave.total_duration_hours)} ${t("leave.summary.hours")}`
                      : leave.calculated_days != null
                      ? `${Number(leave.calculated_days)} ${t("leave.summary.days")}`
                      : "—"}
                  </dd>
                </div>
                {leave.balance_before_days != null || leave.balance_before_hours != null ? (
                  <>
                    <div>
                      <dt>{t("admin.leaves.balanceBefore")}</dt>
                      <dd className="mono">
                        {leave.balance_before_hours != null
                          ? `${Number(leave.balance_before_hours)} saat`
                          : leave.balance_before_days != null
                          ? `${Number(leave.balance_before_days)} gün`
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt>{t("admin.leaves.balanceAfter")}</dt>
                      <dd className="mono">
                        {leave.balance_after_hours != null
                          ? `${Number(leave.balance_after_hours)} saat`
                          : leave.balance_after_days != null
                          ? `${Number(leave.balance_after_days)} gün`
                          : "—"}
                      </dd>
                    </div>
                  </>
                ) : null}
                {leave.proxy_username && (
                  <div>
                    <dt>{t("admin.leaves.proxyUser")}</dt>
                    <dd>
                      {[leave.proxy_name, leave.proxy_surname].filter(Boolean).join(" ")}{" "}
                      ({leave.proxy_username})
                    </dd>
                  </div>
                )}
                {leave.documentation_url && (
                  <div>
                    <dt>{t("admin.leaves.documentationUrl")}</dt>
                    <dd>
                      <a
                        href={leave.documentation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {leave.documentation_url}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              <div className="leave-details-reason">
                <strong>{t("admin.leaves.reasonLabel")}</strong>
                <p>{leave.reason || "—"}</p>
              </div>
              {leave.excuse_detail && (
                <div className="leave-details-reason">
                  <strong>{t("admin.leaves.excuseLabel")}</strong>
                  <p>{leave.excuse_detail}</p>
                </div>
              )}
              {leave.rejection_reason && (
                <div className="leave-details-reason reject-reason">
                  <strong>{t("leave.status.rejected")}:</strong>
                  <p>{leave.rejection_reason}</p>
                </div>
              )}
            </section>

            <section>
              <h4>{t("admin.leaves.sectionApproval")}</h4>
              <dl>
                <div>
                  <dt>{t("leave.overallStatus")}</dt>
                  <dd>{prettyStatus(leave.status, t)}</dd>
                </div>
                <div>
                  <dt>{t("leave.nextStage")}</dt>
                  <dd>
                    {String(leave.status || "").toLowerCase() === "beklemede" && leave.current_stage
                      ? prettyStage(leave.current_stage, t)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>{t("leave.stage.unitSupervisor")}</dt>
                  <dd>{prettyStatus(leave.manager_status, t)}</dd>
                </div>
                <div>
                  <dt>{t("leave.stage.companyManager")}</dt>
                  <dd>{prettyStatus(leave.company_status, t)}</dd>
                </div>
                <div>
                  <dt>{t("leave.stage.hr")}</dt>
                  <dd>{prettyStatus(leave.hr_status, t)}</dd>
                </div>
              </dl>
            </section>

            {isTravel && (leave.travel_destination || leave.travel_project || leave.travel_days || travelExpense) && (
              <section>
                <h4>{t("admin.leaves.sectionTravel")}</h4>
                <dl>
                  {leave.travel_destination && (
                    <div>
                      <dt>{t("admin.leaves.travelDestination")}</dt>
                      <dd>{leave.travel_destination}</dd>
                    </div>
                  )}
                  {leave.travel_project && (
                    <div>
                      <dt>{t("admin.leaves.travelProject")}</dt>
                      <dd>{leave.travel_project}</dd>
                    </div>
                  )}
                  {leave.travel_days != null && (
                    <div>
                      <dt>{t("admin.leaves.travelDays")}</dt>
                      <dd className="mono">{leave.travel_days}</dd>
                    </div>
                  )}
                  {travelExpense?.trip_type && (
                    <div>
                      <dt>{t("admin.leaves.tripType")}</dt>
                      <dd>
                        {travelExpense.trip_type === "international"
                          ? t("admin.leaves.tripInternational")
                          : travelExpense.trip_type === "intracity"
                            ? t("admin.leaves.tripIntracity")
                            : t("admin.leaves.tripDomestic")}
                      </dd>
                    </div>
                  )}
                  {travelExpense && (
                    <>
                      <div>
                        <dt>{t("admin.leaves.transport")}</dt>
                        <dd>
                          {travelExpense.transport_required
                            ? prettyTransport(travelExpense.transport_type)
                            : t("common.no")}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("admin.leaves.accommodation")}</dt>
                        <dd>
                          {travelExpense.accommodation_required
                            ? travelExpense.accommodation_link || t("common.yes")
                            : t("common.no")}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>

                {travelExpense && (
                  <>
                    <h5>{t("admin.leaves.sectionPerDiem")}</h5>
                    <dl>
                      <div>
                        <dt>{t("admin.leaves.perDiemDaily")}</dt>
                        <dd className="mono">
                          {canEditPerDiem ? (
                            <div className="per-diem-edit">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={perDiemDraft}
                                onChange={(e) => setPerDiemDraft(e.target.value)}
                                disabled={savingPerDiem}
                                aria-label={t("admin.leaves.perDiemDaily")}
                              />
                              <span>{travelExpense.per_diem_currency}</span>
                              <button
                                type="button"
                                className="primary"
                                onClick={handleSavePerDiem}
                                disabled={
                                  savingPerDiem ||
                                  String(perDiemDraft) ===
                                    String(travelExpense.per_diem_daily_amount)
                                }
                              >
                                {savingPerDiem
                                  ? t("admin.leaves.perDiemSaving")
                                  : t("admin.leaves.perDiemSave")}
                              </button>
                            </div>
                          ) : (
                            formatMoney(
                              travelExpense.per_diem_daily_amount,
                              travelExpense.per_diem_currency
                            )
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("admin.leaves.perDiemDays")}</dt>
                        <dd className="mono">{travelExpense.per_diem_days ?? "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("admin.leaves.perDiemTotal")}</dt>
                        <dd className="mono">
                          {formatMoney(
                            travelExpense.per_diem_total,
                            travelExpense.per_diem_currency
                          )}
                        </dd>
                      </div>
                    </dl>
                  </>
                )}

                {travelExpense?.has_extra_expenses && expenseItems.length > 0 && (
                  <>
                    <h5>{t("admin.leaves.sectionExpenses")}</h5>
                    <div className="table-wrap">
                      <table className="expense-items-table">
                        <thead>
                          <tr>
                            <th>{t("admin.leaves.expensePayee")}</th>
                            <th>{t("admin.leaves.expenseDate")}</th>
                            <th>{t("admin.leaves.expenseInvoice")}</th>
                            <th>{t("admin.leaves.expenseLimit")}</th>
                            <th>{t("admin.leaves.expensePayable")}</th>
                            <th>{t("admin.leaves.expenseDescription")}</th>
                            <th>{t("admin.leaves.expenseReceipt")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenseItems.map((it) => (
                            <tr key={it.id}>
                              <td>{it.payee_company}</td>
                              <td className="mono">{formatDate(it.expense_date)}</td>
                              <td className="mono">
                                {formatMoney(it.invoice_amount, travelExpense.per_diem_currency)}
                              </td>
                              <td className="mono">
                                {it.limit_amount == null
                                  ? "—"
                                  : formatMoney(it.limit_amount, travelExpense.per_diem_currency)}
                              </td>
                              <td className="mono">
                                {formatMoney(it.payable_amount, travelExpense.per_diem_currency)}
                              </td>
                              <td>{it.description || "—"}</td>
                              <td>
                                <div className="receipt-actions">
                                  <input
                                    ref={(el) => {
                                      fileInputRefs.current[it.id] = el;
                                    }}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    style={{ display: "none" }}
                                    onChange={(e) => handleReceiptChange(it.id, e)}
                                  />
                                  {it.has_receipt_pdf ? (
                                    <>
                                      <button
                                        type="button"
                                        className="ghost small"
                                        onClick={() => handleReceiptDownload(it.id)}
                                        title={it.receipt_pdf_original_name || ""}
                                      >
                                        {t("admin.leaves.expenseReceiptDownload")}
                                      </button>
                                      <button
                                        type="button"
                                        className="ghost small"
                                        disabled={uploadingItemId === it.id}
                                        onClick={() => handleReceiptPick(it.id)}
                                      >
                                        {uploadingItemId === it.id
                                          ? t("admin.leaves.expenseReceiptUploading")
                                          : t("admin.leaves.expenseReceiptReplace")}
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="primary small"
                                      disabled={uploadingItemId === it.id}
                                      onClick={() => handleReceiptPick(it.id)}
                                    >
                                      {uploadingItemId === it.id
                                        ? t("admin.leaves.expenseReceiptUploading")
                                        : t("admin.leaves.expenseReceiptUpload")}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveDetailsModal;
